import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

import { makeStyles } from "@material-ui/core/styles";
import { dataService } from '../../../Services/data/data.service';
import { parseNewDateToDDMMYYYWithoutSeconds } from '../../../Utils/dateHelper';
import { valueTypeCodes, controlTypes, ProjectFilterTypes, resultColumns } from '../../../Config/constants';

// Packages
import { Spin } from "react-cssfx-loading";
import { toast } from 'react-toastify';
import html2canvas from 'html2canvas';

// Components
import TitlePage from '../../Shared/TitlePage';
import DateRangePicker from '../../Shared/DateSelection/DateRangePicker';
import DevExDataTable from "../Results/Components/DevExDataTable";
import BoolToggle from '../../Shared/Buttons/BoolToggle';
import { Box, Tooltip } from '@mui/material';

// Assets
import { Share, CalendarClock, ChevronLeft, Plus, Minus, Timer, ScanBarcode, Image, TextCursorInput, LinkIcon, List, DiamondPlus, ChevronDown, X, Download, ArrowRight } from 'lucide-react';

function GeneralLedgerAccountPage(props) {
    const classes = useStyles();
    const navigate = useNavigate();

    // General
    const [loading, setLoading] = useState(false);
    const [graphProjects, setGraphProjects] = useState([]);
    const [allFilters, setAllFilters] = useState();
    const [allCompanies, setAllCompanies] = useState([]);

    // Report
    const [reportData, setReportData] = useState(null);
    const generalLedgerRef = useRef(null);

    // Account Detail
    const [accountDetailMeasurementRows, setAccountDetailMeasurementRows] = useState([]);
    const [selectedAccountId, setSelectedAccountId] = useState(null);
    const [selectedAccountLinkedDetails, setSelectedAccountLinkedDetails] = useState([]);

    // Datagrid properties
    const [measurementRows, setMeasurementRows] = useState([]);
    const [originalMeasurementRows, setOriginalMeasurementRows] = useState([]); // All measurementrows for current GraphDetail - should not be overwritten
    const [types, setTypes] = useState([]);

    // Company selection
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
    const [filteredCompanies, setFilteredCompanies] = useState(allCompanies);
    const companyDropdownRef = useRef(null);


    // Filtering
    const [dateFiltering, setDateFiltering] = useState(false);
    const [timeRangeDates, setTimeRangeDates] = useState({ fromDate: new Date(Date.now() - 86400000), startDate: new Date(Date.now() - 86400000), toDate: new Date(Date.now()), endDate: new Date(Date.now()) });
    const [selectedProjects, setSelectedProjects] = useState([]);
    const [graphConfigurationName, setGraphConfigurationName] = useState("");
    const [selectedProject, setSelectedProject] = useState([]);
    const [graphData, setGraphData] = useState([]);
    const [labelVisible, setLabelVisible] = useState(true);

    // Date range filters
    const [startDate, setStartDate] = useState();
    const [endDate, setEndDate] = useState();

    //DevExData
    const [selection, setSelection] = useState([]);

    useEffect(() => {
        // set timeRangeDates to the first and last day of the current month when the component mounts (with first day being midnight and last day being 23:59:59)
        const currentDate = new Date();
        const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);
        setTimeRangeDates({ fromDate: firstDay, startDate: firstDay, toDate: lastDay, endDate: lastDay });
    }, [])

    useEffect(() => {
        dataService.getAllCompaniesByUserId(props.Login.userid).then(results => {
            setAllCompanies(results);
            setFilteredCompanies(results);
        });
    }, [])

    useEffect(() => {
        const adjustToUTC = (date) => {
            // Adjust the local Brussels time to UTC by removing the timezone offset
            const utcDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
            return utcDate.toISOString(); // Convert to ISO format
        };

        const fromDate = adjustToUTC(timeRangeDates.fromDate); // Convert to UTC
        const toDate = adjustToUTC(timeRangeDates.toDate); // Convert to UTC

        if (selectedCompanyId) {
            dataService.getReportByCompanyIdAndDateRange(selectedCompanyId, fromDate, toDate).then(results => {
                setReportData(results);
            }).catch((error) => {
                console.log('Error ', error);
                toast.error("Something went wrong", { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
            });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const isValidDate = (date) => date instanceof Date && !isNaN(date);
        const isAfter2000 = (date) => date > new Date('2000-01-01');

        if (
            selectedCompanyId &&
            isValidDate(timeRangeDates.fromDate) &&
            isValidDate(timeRangeDates.toDate) &&
            isAfter2000(timeRangeDates.fromDate) &&
            isAfter2000(timeRangeDates.toDate)
        ) {
            getCompanyReportData(selectedCompanyId);
        }
    }, [timeRangeDates]); // eslint-disable-line react-hooks/exhaustive-deps


    useEffect(() => {
        dataService.getTypes().then(results => {
            setTypes(results.sort((a, b) => a.description.localeCompare(b.description)));
        });
    }, [props])

    useEffect(() => {
        const isValidDate = (date) => date instanceof Date && !isNaN(date);
        const isAfter2000 = (date) => date > new Date('2000-01-01');

        if (isValidDate(timeRangeDates.fromDate) &&
            isValidDate(timeRangeDates.toDate) &&
            isAfter2000(timeRangeDates.fromDate) &&
            isAfter2000(timeRangeDates.toDate)
        ) {
            onFiltersChange();
        }
    }, [timeRangeDates]); // eslint-disable-line react-hooks/exhaustive-deps


    const handleCompanyInputChange = (e) => {
        const input = e.target.value;
        setFilteredCompanies(
            allCompanies.filter(company =>
                company.name.toLowerCase().includes(input.toLowerCase())
            )
        );
        setIsCompanyDropdownOpen(true); // Open dropdown on typing
    };

    const handleCompanySelect = (company) => {
        setSelectedCompanyId(company.id); // Set selected company name
        setIsCompanyDropdownOpen(false); // Close dropdown
        getCompanyReportData(company.id);
    };

    const toggleCompanyDropdown = () => {
        setIsCompanyDropdownOpen(prev => !prev);
    };

    const handleClickOutsideCompanySelection = (event) => {
        if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target)) {
            setIsCompanyDropdownOpen(false); // Close dropdown when clicking outside
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutsideCompanySelection);
        return () => {
            document.removeEventListener('mousedown', handleClickOutsideCompanySelection);
        };
    }, []);

    const clearCompanySelection = () => {
        setSelectedCompanyId(null);
        setFilteredCompanies(allCompanies);
        setMeasurementRows([]);
        setIsCompanyDropdownOpen(true);
    };

    const getCompanyReportData = (companyId) => {
        setSelectedAccountId(null);
        const adjustToUTC = (date) => {
            // Adjust the local Brussels time to UTC by removing the timezone offset
            const utcDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
            return utcDate.toISOString(); // Convert to ISO format
        };

        const fromDate = adjustToUTC(timeRangeDates.fromDate); // Convert to UTC
        const toDate = adjustToUTC(timeRangeDates.toDate); // Convert to UTC

        dataService.getReportByCompanyIdAndDateRange(companyId, fromDate, toDate).then(results => {
            setReportData(results);
            dataService.getMeasurementsByCompanyIdAndDateRange(companyId, fromDate, toDate).then(measurements => {
                let tempMeasurementRows = measurements.flat()
                tempMeasurementRows = (tempMeasurementRows.map(
                    y => ({
                        checked: true,
                        accountId: y.accountId,
                        companyId: y.companyId,
                        companyName: y.companyName,
                        detailId: y.projectDetailId,
                        projectId: y.projectId,
                        toDate: y.toDate,
                        fromDate: y.fromDate,
                        color: y.color,
                        id: y.id,
                        typeId: y.typeId,
                        valueType: getValueType(y.valueTypeId),
                        valueTypeId: y.valueTypeId,
                        qrCode: y.qrCode,
                        projectName: y.projectName,
                        price: y.price,
                        resourceName: y.resourceName,
                        description: y.description,
                        startTime: y.startTime,
                        stopTime: y.stopValue,
                        insertedOn: y.startTime,
                        updatedOn: y.stopTime,
                        measurementSessionId: y.measurementSessionId,
                        measurementValue: getMeasurementValue(y.typeId, y.textValue, y.integerValue, y.dateValue, y.dataListValue, y.imageLink, y.externalApiValues),
                        integerValue: y.integerValue,
                        running: y.running,
                        thumbnail: y.thumbnail,
                        imageId: y.imageId,
                        commentText: y.commentText,
                        commentImage: y.commentImage,
                        commentId: y.commentId,
                        keyValue: y.keyValue,
                        eventPrice: getEventPrice(y),
                    })
                ))

                setMeasurementRows(tempMeasurementRows);
                setOriginalMeasurementRows(measurements);
            }).catch((error) => {
                console.log('Error ', error);
                toast.error("Something went wrong", { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
            });
        }).catch((error) => {
            console.log('Error ', error);
            toast.error("Something went wrong", { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
        });
    };

    const getValueType = (id) => {
        switch (id) {
            case valueTypeCodes.VA:
                return 'Value added';
            case valueTypeCodes.NVA:
                return 'No value added';
            case valueTypeCodes.W:
                return 'Waste';
            default:
                return 'Unknown'
        }
    }

    const getMeasurementValue = (typeId, text, int, time, dataListValue, imageLink, externalApiValue) => {
        switch (typeId) {
            case 1: return int;
            case 2:
                if (int > 0) {
                    return time;
                } else {
                    return '';
                }
            case 3: return text;
            case 4: return dataListValue;
            case 5: return text;
            case 6: return imageLink;
            case 7: return externalApiValue;
            default: return ''
            // eslint-disable-next-line 
        }
    }

    const getEventPrice = (measurement) => {
        if (measurement.price === null || measurement.price === undefined || measurement.price == 0) {
            return 0;
        } else {
            if (measurement.typeId === controlTypes.STOPWATCH) {
                const startTime = new Date(measurement.startTime);
                const stopTime = new Date(measurement.stopTime);
                const durationInSeconds = (stopTime - startTime) / 1000; // Convert to seconds
                return parseFloat((measurement.price / 3600) * durationInSeconds.toFixed(2));
            } else {
                return measurement.price;
            }
        }
    };

    const roundToSecond = (date) => {
        return new Date(Math.round(date.getTime() / 1000) * 1000); // Round to nearest second
    };

    // Convert seconds to hh:mm:ss
    function convertHMS(value) {
        const sec = parseInt(value, 10); // convert value to number if it's string
        let hours = Math.floor(sec / 3600); // get hours
        let minutes = Math.floor((sec - (hours * 3600)) / 60); // get minutes
        let seconds = sec - (hours * 3600) - (minutes * 60); //  get seconds
        // add 0 if value < 10; Example: 2 => 02
        if (hours < 10) { hours = "0" + hours; }
        if (minutes < 10) { minutes = "0" + minutes; }
        if (seconds < 10) { seconds = "0" + seconds; }
        return hours + ':' + minutes + ':' + seconds; // Return is HH : MM : SS
    }

    const getLatestStartTimeElement = (data) => {
        //We check the latest measurement -> this is how we will decide from where to start the 'last 24h'
        if (data !== null && data.length > 0) {
            return data.reduce((latest, current) => {
                const latestTime = new Date(latest.startTime);
                const currentTime = new Date(current.startTime);

                // Check if current startTime is later than the latest
                return currentTime > latestTime ? current : latest;
            });
        } else {
            return null;
        }
    };

    const setDateRange = (startDate, endDate) => {
        setStartDate(startDate);
        setEndDate(endDate);
        if (startDate && endDate) {
            setDateFiltering(true);
            setTimeRangeDates({ fromDate: new Date(startDate), startDate: new Date(startDate), toDate: new Date(endDate), endDate: new Date(endDate) });
        } else {
            resetDateRangeFilter()
        }
    }

    const handleSetStartDate = (event) => {
        if (event !== startDate) {
            setStartDate(event);
            setDateRange(event, endDate);
        }
    }
    const handleSetEndDate = (event) => {
        if (event !== endDate) {
            setEndDate(event);
            setDateRange(startDate, event);
        }
    }

    const resetDateRangeFilter = () => {
        // Set timeRange to 24 hours before last measurement
        let element = getLatestStartTimeElement(originalMeasurementRows);
        let from = timeRangeDates.fromDate;
        let to = timeRangeDates.toDate;
        let tempTimeRangeDates = { fromDate: new Date(from), startDate: new Date(from), toDate: to, endDate: to };

        if (element !== null && element !== undefined) {
            to = new Date(element.startTime);
            from = new Date(element.startTime);
            from.setHours(from.getHours() - 24);
            const formattedDate = from.toString();
            from = formattedDate;
            tempTimeRangeDates = { fromDate: new Date(from), startDate: new Date(from), toDate: to, endDate: to };
            setStartDate(new Date(from));
            setEndDate(new Date(to));
            setTimeRangeDates(tempTimeRangeDates);
        }
    }

    //DevEx functions
    const onSelectionChange = (e) => {
        var tempRows = JSON.parse(JSON.stringify(measurementRows));
        const selectedIds = e.map(index => tempRows[index].id);
        setSelection(e);
        const updatedArray = tempRows.map(item =>
            selectedIds.includes(item.id)
                ? { ...item, checked: true }
                : { ...item, checked: false }
        );
    }

    const onFiltersChange = (filters) => {
        let currentFilters;

        if (filters) {
            currentFilters = filters;
            setAllFilters(filters);
        } else {
            currentFilters = allFilters;
        }
        let varFilteredResults = JSON.parse(JSON.stringify(originalMeasurementRows)).sort((a, b) => new Date(b.insertedOn) - new Date(a.insertedOn)); // Clone original rows

        varFilteredResults = varFilteredResults.filter(row => {
            const startTime = roundToSecond(new Date(row.insertedOn)); // Convert to Date
            return startTime >= timeRangeDates.fromDate && startTime <= timeRangeDates.toDate; // Check range
        });

        varFilteredResults.forEach((x) => (x.checked = true)); // Reset checked state

        // Uncheck all rows that have a date outside of date indicated in dateRangeSlider
        varFilteredResults.filter(row => {
            const startTime = new Date(row.insertedOn); // Convert to Date
            return startTime >= timeRangeDates.endDate || startTime <= timeRangeDates.startDate; // Check range
        }).forEach((x) => (x.checked = false));

        currentFilters && currentFilters.forEach((filter) => {
            switch (filter.columnName) {
                case "companyName":
                    varFilteredResults
                        .filter((x) => !x.companyName.toLowerCase().includes(filter.value.toLowerCase()))
                        .forEach((x) => (x.checked = false));
                    break;
                case "projectName":
                    varFilteredResults
                        .filter((x) => !x.projectName.toLowerCase().includes(filter.value.toLowerCase()))
                        .forEach((x) => (x.checked = false));
                    break;
                case "resourceName":
                    if (varFilteredResults.some(x => !x.resourceName.toLowerCase().includes(filter.value.toLowerCase()))) {
                        varFilteredResults.filter(vfr => !vfr.resourceName.toLowerCase().includes(filter.value.toLowerCase())).map((vfr) => {
                            vfr.checked = false;
                        })
                    }
                    break;
                case "description":
                    if (varFilteredResults.some(x => !x.description.toLowerCase().includes(filter.value.toLowerCase()))) {
                        varFilteredResults.filter(vfr => !vfr.description.toLowerCase().includes(filter.value.toLowerCase())).map((vfr) => {
                            vfr.checked = false;
                        })
                    }
                    break;
                case "valueType":
                    var valueType = null;
                    var waste = "waste";
                    var valueAdded = "value added";
                    var noValueAdded = "no value added";

                    if (waste.includes(filter.value.toLowerCase())) {
                        valueType = valueTypeCodes.W;
                    } else if (valueAdded.includes(filter.value.toLowerCase()) && !filter.value.toLowerCase().includes('n')) {
                        valueType = valueTypeCodes.VA;
                    } else if (noValueAdded.includes(filter.value.toLowerCase()) && filter.value.toLowerCase().includes('n')) {
                        valueType = valueTypeCodes.NVA;
                    }
                    if (valueType !== null) {
                        varFilteredResults.filter(x => x.valueTypeId !== valueType).map((vfr) => {
                            vfr.checked = false;
                        });
                    }
                    break;
                case "keyValue":
                    if (filter.value !== '' && filter.value !== undefined && filter.value !== null) {
                        if (varFilteredResults.some(x => !(x.keyValue && x.keyValue.toLowerCase().includes(filter.value.toLowerCase())))) {
                            varFilteredResults.filter(vfr => !(vfr.keyValue && vfr.keyValue.toLowerCase().includes(filter.value.toLowerCase()))).map((vfr) => {
                                vfr.checked = false;
                            })
                        }
                    }
                    break;
                case "startTime":
                    if (filter.value !== '' && filter.value !== undefined && filter.value !== null) {
                        if (varFilteredResults.some(x => !(x.keyValue && x.keyValue.toLowerCase().includes(filter.value.toLowerCase())))) {
                            varFilteredResults.filter(vfr => !(vfr.keyValue && vfr.keyValue.toLowerCase().includes(filter.value.toLowerCase()))).map((vfr) => {
                                vfr.checked = false;
                            })
                        }
                    }

                    break;
                case "measurementValue":
                    if (filter.value !== '' && filter.value !== undefined && filter.value !== null) {
                        if (varFilteredResults.some(x => !(x.measurementValue && x.measurementValue !== undefined && x.measurementValue !== "" && x.measurementValue.toString().toLowerCase().includes(filter.value.toLowerCase())))) {
                            varFilteredResults.filter(vfr => !(vfr.measurementValue && vfr.measurementValue !== undefined && vfr.measurementValue !== "" && vfr.measurementValue.toString().toLowerCase().includes(filter.value.toLowerCase()))).map((vfr) => {
                                vfr.checked = false;
                            })
                        }
                    }

                    break;
                default:
                    ;
            }

        });

        // GENERAL
        setMeasurementRows(varFilteredResults);
        const indexes = varFilteredResults
            .map((item, index) => (item.checked ? index : -1))
            .filter((index) => index !== -1);
        setSelection(indexes);

        let tempGraphProjects = [...graphProjects]; // Clone the projects array
        let fromDate = timeRangeDates.fromDate;
        let toDate = timeRangeDates.toDate;

        tempGraphProjects = tempGraphProjects.map(project => {
            return {
                ...project,
                details: project.details.map(detail => {
                    return {
                        ...detail,
                        measurements: detail.measurements.filter(measurement =>
                            roundToSecond(new Date(measurement.insertedOn)) > fromDate && roundToSecond(new Date(measurement.insertedOn)) < toDate
                        )
                    };
                })
            };
        });
    };

    const limitString = (str, limit) => {
        if (str.length > limit) {
            return str.substring(0, limit) + "...";
        } else {
            return str;
        }
    }

    const formatPrice = (price) => {
        if (price === null || price === undefined || price === "") {
            return "0,00";
        } else {
            return price
                .toFixed(2) // Ensure two decimal places
                .replace('.', ',') // Replace decimal point with comma
                .replace(/\B(?=(\d{3})+(?!\d))/g, ' '); // Add space as thousands separator
        }
    };


    const handleDownloadReport = async () => {
        let element = generalLedgerRef.current;
        let fileName = 'generalLedgerAccount.jpg';
        const canvas = await html2canvas(element);

        const data = canvas.toDataURL('image/jpg');
        const link = document.createElement('a');

        if (typeof link.download === 'string') {
            link.href = data;
            link.download = fileName;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            window.open(data);
        }
    };

    const getEmptyStateTitle = () => {
        if (selectedCompanyId === null) {
            return "Select a company";
        } else if (reportData === null) {
            return "No data available";
        }
    }
    const getEmptyStateSubtitle = () => {
        if (selectedCompanyId === null) {
            return "Please select a company to view the report";
        } else if (reportData === null) {
            return "No data available for the selected date range";
        }
    }

    const showAccountDetail = (accountId) => {
        setSelectedAccountId(accountId)

        let accountLinkedDetails = [];
        let tempAccountDetailMeasurementRows = measurementRows.filter(measurement => measurement.accountId === accountId);

        if (tempAccountDetailMeasurementRows.length > 0) {
            tempAccountDetailMeasurementRows.forEach(measurement => {
                let detail = accountLinkedDetails.find(detail => detail.name === measurement.description);
                if (detail) {
                    detail.totalPrice += measurement.eventPrice;
                } else {
                    accountLinkedDetails.push({ name: measurement.description, totalPrice: measurement.eventPrice });
                }
            });
        }

        // order by name
        accountLinkedDetails.sort((a, b) => a.name.localeCompare(b.name));

        setSelectedAccountLinkedDetails(accountLinkedDetails);
        setAccountDetailMeasurementRows(tempAccountDetailMeasurementRows);
    };

    const getSelectedAccountName = () => {
        if (selectedAccountId) {
            let costAccount = reportData.costAccountCategoryResults
                ?.flatMap((category) => category.accountResults) // Flatten all accountResults arrays into one
                .find((result) => result.id === selectedAccountId); // Find the desired account

            let revenueAccount = reportData.revenueAccountCategoryResults
                ?.flatMap((category) => category.accountResults) // Flatten all accountResults arrays into one
                .find((result) => result.id === selectedAccountId); // Find the desired account

            return (costAccount?.name || revenueAccount?.name);
        } else {
            return "All";
        }
    };

    const getSelectedAccountTotalPrice = () => {
        if (selectedAccountId) {
            let costAccount = reportData.costAccountCategoryResults
                ?.flatMap((category) => category.accountResults) // Flatten all accountResults arrays into one
                .find((result) => result.id === selectedAccountId); // Find the desired account

            let revenueAccount = reportData.revenueAccountCategoryResults
                ?.flatMap((category) => category.accountResults) // Flatten all accountResults arrays into one
                .find((result) => result.id === selectedAccountId); // Find the desired account

            return revenueAccount?.total
                ? `+${revenueAccount.total.toFixed(2)}`
                : (costAccount?.total || 0).toFixed(2);
        } else {
            return reportData.totalProfit;
        }
    };

    const isSelectedAccountRevenue = () => {
        if (selectedAccountId) {
            let revenueAccount = reportData.revenueAccountCategoryResults
                ?.flatMap((category) => category.accountResults) // Flatten all accountResults arrays into one
                .find((result) => result.id === selectedAccountId); // Find the desired account

            return revenueAccount !== undefined;
        } else {
            return false;
        }
    };

    const getTimeRangeProgressPercentage = () => {
        let tempStartTime = new Date(timeRangeDates.fromDate).getTime(); // Convert to timestamp
        let tempStopTime = new Date(timeRangeDates.toDate).getTime();    // Convert to timestamp
        let currentTime = Date.now(); // Current timestamp

        if (currentTime < tempStartTime) {
            // Before the time range starts
            return 0;
        }
        if (currentTime > tempStopTime) {
            // After the time range ends
            return 100;
        }

        // Calculate total duration and elapsed time
        let totalDuration = tempStopTime - tempStartTime;
        let elapsedTime = currentTime - tempStartTime;

        // Calculate percentage
        let percentage = (elapsedTime / totalDuration) * 100;

        // Round to 2 decimal places
        return parseFloat(percentage.toFixed(2));
    };

    const getAccountDetailPercentage = (price) => {
        // Get the total account price
        let totalAccountPrice = getSelectedAccountTotalPrice();

        // Ensure the totalAccountPrice is not zero to avoid division by zero
        if (totalAccountPrice === 0) {
            throw new Error("Total account price is zero, cannot calculate percentage.");
        }

        // Calculate the percentage
        let percentage = (price / totalAccountPrice) * 100;

        // Round to 2 decimal places and ensure absolute value
        percentage = Math.abs(Math.round(percentage * 100) / 100);

        return percentage;
    };

    return (
        <React.Fragment>
            <TitlePage Text="Graph" backbutton={true} {...props} />
            {loading &&
                <div className={classes.loadingChart}><Spin width="100px" height="100px" color="#C3996B"></Spin></div>

            }
            <Box sx={{
                maxWidth: '90%', display: 'flex', justifyContent: 'center', flexDirection: 'column', marginRight: "auto",
                marginLeft: "auto", marginTop: "148px"
            }} >

                <Box className={classes.container}>
                    <div className={classes.header__titleRow}>
                        <h1 className={classes.header__title}>General Ledger Account</h1>
                        {/* <div className={classes.buttonContainer}>
                            <button className={classes.primaryButton} onClick={() => navigateToModify()}>Edit graph</button>
                        </div> */}
                    </div>
                    <div className={classes.tableManagementContainer}>
                        <div className={classes.tableManagementContainer__start}>
                            {/* <div className={classes.searchContainer}>
                                <input placeholder="Search for graphs" className={classes.search__input} type="text" value={getFilterValue(GraphFilterTypes.GENERAL)} onChange={(e) => updateFilters(GraphFilterTypes.GENERAL, e)} />
                                <Search size={16} className={classes.inputIcon} />
                            </div> */}
                            <div className={classes.dropdownContainer} ref={companyDropdownRef}>
                                <input
                                    placeholder="Select a company"
                                    className={classes.dropdown__input}
                                    type="text"
                                    value={selectedCompanyId ? allCompanies.find(company => company.id === selectedCompanyId).name : ""}
                                    onChange={handleCompanyInputChange}
                                    onClick={toggleCompanyDropdown} // Open dropdown on click
                                />
                                {!selectedCompanyId ? (
                                    <ChevronDown
                                        size={16}
                                        className={classes.dropdown__inputIcon}
                                        style={{
                                            transform: `translateY(-50%) rotate(${isCompanyDropdownOpen ? '180deg' : '0deg'})`,
                                            transition: 'transform 0.2s ease-in-out', // Smooth rotation animation
                                        }}
                                        onClick={toggleCompanyDropdown}
                                    />
                                ) : (
                                    <button
                                        onClick={(e) => clearCompanySelection(e)}
                                        className={classes.clearInputButton}
                                        style={{
                                            width: "24px",
                                            height: "24px",
                                        }}
                                    >
                                        <X
                                            className={classes.dropdown__clearInputIcon}
                                            style={{
                                                width: "16px",
                                                height: "16px",
                                            }}
                                        />
                                    </button>
                                    // <X size={16} className={classes.dropdown__inputIcon} style={{ transform: `translateY(-50%)` }} onClick={() => setSelectedCompanyId(null)} />
                                )}
                                {isCompanyDropdownOpen && (
                                    <div className={classes.dropdown__menu}>
                                        {filteredCompanies.map(company => (
                                            <div
                                                key={company.id}
                                                className={classes.dropdown__menuItem}
                                                onClick={() => handleCompanySelect(company)}
                                            >
                                                {company.name}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <DateRangePicker setDateRange={setDateRange} resetDateRangeFilter={resetDateRangeFilter} startDate={timeRangeDates.fromDate} endDate={timeRangeDates.endDate} handleSetStartDate={handleSetStartDate} handleSetEndDate={handleSetEndDate} />
                            {/* <div className={classes.searchContainer}>
                                <input placeholder="Select a company" className={classes.search__input} type="text" value={selectedCompanyId} onChange={(e) => setSelectedCompanyId(e.target.value)} />
                                <ChevronDown size={16} className={classes.inputIcon} />
                            </div> */}
                        </div>
                        <div className={classes.tableManagementContainer__end}>
                            {selectedCompanyId !== null && reportData !== null && (
                                <button
                                    className={classes.tableManagementContainer__primaryIconButton}
                                    onClick={handleDownloadReport}
                                >
                                    <Download className={classes.iconButton__icon} />
                                    Download report
                                </button>
                            )}
                        </div>
                    </div>
                    <div className={classes.reportContainer}>
                        {reportData === null || selectedCompanyId === null ? (
                            <div className={classes.emptyStateContainer}>
                                <div className={classes.emptyStateCard}>
                                    <p className={classes.emptyStateCard__title}>{getEmptyStateTitle()}</p>
                                    <p className={classes.emptyStateCard__subtitle}>{getEmptyStateSubtitle()}</p>
                                </div>
                            </div>
                        ) : (

                            <div className={`${classes.reportContainer__horizontalContainer} ${selectedAccountId ? "" : classes.removeGap}`}>
                                <div className={classes.reportContainer__report} ref={generalLedgerRef}>
                                    <div className={classes.report__header}>
                                        <h2 className={classes.report__title}>P&L statement for {reportData.companyName}</h2>
                                        <div className={classes.report__timeRangeContainer}>
                                            <p className={classes.report__subtitle}>{parseNewDateToDDMMYYYWithoutSeconds(timeRangeDates.fromDate)} <ArrowRight height={16} width={16} strokeWidth={1.6} /> {parseNewDateToDDMMYYYWithoutSeconds(timeRangeDates.toDate)}</p>
                                            <Tooltip title={getTimeRangeProgressPercentage() + "% of the current period has elapsed"} placement="right" arrow>
                                                <div className={classes.report__timeRangeIndicatorContainer}>
                                                    <div style={{ width: getTimeRangeProgressPercentage() + "%" }} className={classes.report__timeRangeIndicator} />
                                                </div>
                                            </Tooltip>
                                        </div>
                                    </div>
                                    <div className={classes.report__subdivision}>
                                        <h3 className={classes.chip__green}>Revenue</h3>
                                        {reportData.revenueAccountCategoryResults.map(category => (
                                            <div className={classes.accountCategory} key={category.accountCategoryId}>
                                                <div className={classes.accountCategory__header}>
                                                    <h4 className={classes.accountCategory__title}>{category.name}</h4>
                                                    <p className={classes.accountCategory__total}>{"+" + formatPrice(category.total)} EUR</p>
                                                </div>
                                                <div className={classes.accountCategory__accounts}>
                                                    {category.accountResults.map(account => (
                                                        <button
                                                            onClick={() => showAccountDetail(account.id)}
                                                            className={`${classes.account} ${selectedAccountId === account.id ? classes.account__highlighted : ''}`}
                                                            key={account.id}
                                                        >
                                                            <p className={classes.account__title}>{account.name}</p>
                                                            <p>{"+" + formatPrice(account.total)} EUR</p>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className={classes.report__subdivision}>
                                        <h3 className={classes.chip__red}>Cost of Goods Sold (COGS)</h3>
                                        {reportData.costAccountCategoryResults.map(category => (
                                            <div className={classes.accountCategory} key={category.accountCategoryId}>
                                                <div className={classes.accountCategory__header}>
                                                    <h4 className={classes.accountCategory__title}>{category.name}</h4>
                                                    <p className={classes.accountCategory__total}>{category.total == 0 ? "-" : ""}{formatPrice(category.total)} EUR</p>
                                                </div>

                                                <div className={classes.accountCategory__accounts}>
                                                    {category.accountResults.map(account => (
                                                        <button
                                                            onClick={() => showAccountDetail(account.id)}
                                                            className={`${classes.account} ${selectedAccountId === account.id ? classes.account__highlighted : ''}`}
                                                            key={account.id}
                                                        >
                                                            <p className={classes.account__title}>{account.name}</p>
                                                            <p>{(account.total == 0 ? "-" : "") + formatPrice(account.total)} EUR</p>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className={classes.report__total}>
                                        <p className={classes.total__title}>Gross Profit <span className={classes.total__breakdown}>(Total Revenue - Total COGS)</span></p>
                                        <p className={classes.total__price}>{(reportData.totalProfit > 0 ? "+" : "") + formatPrice(reportData.totalProfit)} EUR</p>
                                    </div>
                                </div>
                                <div className={`${classes.reportContainer__accountDetail} ${selectedAccountId ? classes.active : ""}`}>
                                    <div className={classes.accountDetail__header}>
                                        <h2 className={classes.accountDetail__title}>{getSelectedAccountName()}</h2>
                                        <button onClick={() => setSelectedAccountId(null)} className={classes.accountDetail__closeButton}>
                                            <X size={19} />
                                        </button>
                                    </div>
                                    <div className={classes.accountDetail__content}>
                                        <div className={classes.accountDetail__details}>
                                            {selectedAccountLinkedDetails.map(detail => (
                                                <Tooltip title={getAccountDetailPercentage(detail.totalPrice) + "%"} placement="right" arrow>
                                                    <div className={classes.accountDetail__detail} key={detail.name}>
                                                        <p className={classes.accountDetail__detailTitle}>{limitString(detail.name, 30)}</p>
                                                        <p className={classes.accountDetail__detailPrice}>{(isSelectedAccountRevenue() ? "+" : "-") + formatPrice(detail.totalPrice)} EUR</p>
                                                    </div>
                                                </Tooltip>
                                            ))}
                                            <div className={classes.accountDetail__total}>
                                                <p className={classes.accountDetail__totalTitle}>Total</p>
                                                <p className={classes.accountDetail__totalPrice}>{getSelectedAccountTotalPrice()} EUR</p>
                                            </div>
                                        </div>
                                        <div className={classes.accountDetail__disclaimer}>
                                            <p className={classes.accountDetail__disclaimerText}>You can see all measurements for the selected account below.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {measurementRows.length > 0 &&
                            <div className={classes.devExDataTableContainer}>
                                <div className={classes.devExDataTable__header}>
                                    <h2 className={classes.devExDataTable__title}>{getSelectedAccountName()} measurements</h2>
                                    {selectedAccountId &&
                                        <button className={classes.devExDataTable__secondaryButton} onClick={() => setSelectedAccountId(null)}>Show all measurements</button>
                                    }
                                </div>
                                <DevExDataTable data={selectedAccountId ? measurementRows.filter(measurement => measurement.accountId === selectedAccountId) : measurementRows} columns={resultColumns} onSelectionChange={onSelectionChange} selection={selection} handleFiltersChange={onFiltersChange}></DevExDataTable>
                            </div>
                        }
                    </div>
                </Box>
            </Box>
        </React.Fragment >
    )
}

export default GeneralLedgerAccountPage;

const useStyles = makeStyles((theme) => ({
    loadingChart: {
        height: '100vh',
        width: '100%',
        top: '0px',
        zIndex: '999',
        position: 'fixed',
        borderRadius: "8px",
        backgroundColor: 'rgba(156,156,156,0.5)',
        display: 'flex',
        alignContent: 'center',
        alignItems: 'center',
        justifyContent: 'center',
    },

    container: {
        width: "100%",
        boxSizing: "border-box",
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        borderRadius: "8px",
        marginBottom: '24px',
        marginRight: "auto",
        marginLeft: "auto",
    },

    header__titleRow: {
        width: "100%",
        minHeight: "40px",

        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
        flexWrap: "wrap",

        marginBottom: "16px",

        [theme.breakpoints.down('sm')]: {
            flexDirection: "column",
            alignItems: "flex-start",
        }
    },

    header: {
        width: '100%',

        display: 'flex',
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "8px",

        backgroundColor: "#C3996B",
        color: 'white',

        borderTopRightRadius: '8px',
        borderTopLeftRadius: '8px',

        padding: "16px",
    },

    header__titleDetailRow: {
        width: "100%",

        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
        flexWrap: "wrap",
    },

    header__projectTitle: {
        color: "white",
    },

    header__description: {
        fontFamily: "Lato, sans-serif",
        fontSize: "16px",
        margin: "0px",
    },

    header__detailContainer: {
        display: "flex",
        gap: "24px",
    },

    header__detail: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },

    header__detailIcon: {
        width: "20px",
        height: "20px",

        strokeWidth: "1.4px",

        display: "block",
    },

    header__detailText: {
        margin: "0px",
    },

    header__published: {
        display: "flex",
        alignItems: "center",
        gap: "8px",

    },

    statusIndicator: {
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        display: "inline-block",
    },

    // Style for published indicator (green)
    published: {
        backgroundColor: "#5CC958",
    },

    // Style for not published indicator (red)
    notPublished: {
        backgroundColor: "#D84949",
    },

    tableManagementContainer: {
        width: "100%",
        height: "fit-content",

        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
        flexWrap: "wrap-reverse",

        marginBottom: "8px",
    },

    tableManagementContainer__start: {
        width: "fit-content",

        display: "flex",
        alignItens: "center",
        gap: "8px",

        flexWrap: "wrap",
    },

    dropdownContainer: {
        width: "240px",
        position: 'relative',
    },

    dropdown__input: {
        width: '100%',
        height: '32px',
        boxSizing: 'border-box',

        display: 'flex',
        alignItems: 'center',

        padding: '0px 8px',
        paddingRight: '32px',

        borderRadius: "4px",
        border: "1px solid #DCDCDC",

        fontSize: '14px',
        fontFamily: 'Lato, sans-serif',
        fontWeight: 'normal',
        color: "#0C0E29",

        backgroundColor: "#FFFFFF",

        "&:focus": {
            outline: "none",
            border: "1px solid #C3996B",
        },
    },

    clearInputButton: {
        width: "24px",
        height: "24px",

        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        position: "absolute",
        top: "50%",
        right: "4px",
        transform: "translateY(-50%)",

        borderRadius: "2px",
        border: "none",

        backgroundColor: "transparent",
        color: "#0C0E29",

        padding: "0",

        "&:hover": {
            cursor: "pointer",
            color: "#D84949",
            backgroundColor: "#EDEDED",
        }
    },

    dropdown__clearInputIcon: {
        width: "16px",
        height: "16px",
        display: "block",

        strokeWidth: "1.4px",
        color: "inherit",
    },

    dropdown__inputIcon: {
        color: "#0C0E29",
        position: 'absolute',
        top: '50%',
        right: '8px',
    },

    dropdown__menu: {
        width: '100%',
        maxHeight: '200px',
        overflowY: 'auto',
        position: 'absolute',
        top: 'calc(100% + 4px)',
        left: '0',
        zIndex: '999',
        backgroundColor: '#FFFFFF',
        borderRadius: '4px',
        border: '1px solid #DCDCDC',
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',

        display: 'flex',
        flexDirection: 'column',

        padding: '4px',

        "&::-webkit-scrollbar": {
            width: "8px",
        },

        "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#C3996B",
            borderRadius: "8px",
        },

        "&::-webkit-scrollbar-track": {
            backgroundColor: "#F4F4F4",
            borderRadius: "8px",
        },

        "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#C3996B",
        },
    },

    dropdown__menuItem: {
        width: '100%',
        height: '32px',
        boxSizing: 'border-box',

        display: 'flex',
        alignItems: 'center',

        padding: '0px 8px',
        borderRadius: '4px',
        cursor: 'pointer',

        fontSize: '14px',
        fontFamily: 'Lato, sans-serif',
        fontWeight: 'normal',
        color: "#0C0E29",
        backgroundColor: '#FFFFFF',

        "&:hover": {
            backgroundColor: '#F4F4F4',
        },

        "&:active": {
            backgroundColor: '#EDEDED',
        },

        "&:focus": {
            outline: "none",
        },
    },

    tableManagementContainer__end: {
        width: "fit-content",

        display: "flex",
        alignItens: "center",
        gap: "8px",
    },

    tableManagementContainer__primaryIconButton: {
        height: "32px",
        padding: "0 12px",
        paddingLeft: "6px",

        border: "none",
        borderRadius: "4px",

        fontSize: "16px",
        color: "white",
        backgroundColor: "#C3996B",

        display: "flex",
        gap: "4px",
        alignItems: "center",

        transition: "all 0.2s ease-in-out",

        "&:hover": {
            backgroundColor: "#a77d4f",
            color: "white",
            cursor: "pointer",
        }
    },

    iconButton__icon: {
        height: "20px",
        width: "20px",

        display: "block",
    },

    noGraphContainer: {
        width: "400px",

        margin: "auto",
        marginTop: "125px",
        padding: "16px",

        backgroundColor: "white",
        border: "1px solid #EDEDED",
        borderRadius: "8px",
    },

    noGraphContainer__text: {
        fontColor: "#0C0E29",
        fontSize: "16px",
        fontFamily: "Lato, sans-serif",
        margin: "0",
        textAlign: "center",
    },

    reportContainer: {
        width: "100%",
        minHeight: "400px",

        position: "relative",
        zIndex: "500",


        borderRadius: "8px",
        marginBottom: "16px",
        boxSizing: "border-box",
    },

    reportContainer__horizontalContainer: {
        width: "100%",

        display: "flex",
        flexDirection: "row",
        gap: "16px",
    },

    removeGap: {
        transition: "gap 0.2s ease-in-out",
        gap: "0px",
    },

    reportContainer__report: {
        width: "100%",

        display: "flex",
        flexDirection: "column",

        backgroundColor: "white",

        borderRadius: "8px",
        border: "1px solid #EDEDED",

        padding: "8px",
    },

    report__header: {
        width: "fit-content",
        boxSizing: "border-box",

        display: "flex",
        flexDirection: "column",
        gap: "8px",

        padding: "8px",
        marginBottom: "16px",
    },

    report__title: {
        fontFamily: "Lato, sans-serif",
        fontSize: "24px",
        margin: "0px",

        color: "#0C0E29",
    },

    report__timeRangeContainer: {
        width: "fit-content",

        position: "relative",

        display: "flex",
        flexDirection: "column",
        gap: "8px",
    },

    report__timeRangeIndicatorContainer: {
        width: "100%",
        height: "8px",

        backgroundColor: "#EDEDED",

        borderRadius: "4px",
    },

    report__timeRangeIndicator: {
        height: "8px",

        backgroundColor: "#5CC958",

        borderRadius: "4px",

        ":hover": {
            backgroundColor: "red",
            top: "12px",
        },
    },

    report__subtitle: {
        width: "fit-content",

        display: "flex",
        alignItems: "center",
        gap: "4px",

        fontFamily: "Lato, sans-serif",
        fontSize: "16px",
        margin: "0px",

        color: "#0C0E29",
        opacity: "0.6",
    },

    report__subdivision: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "16px",

        marginBottom: "8px",
    },

    accountCategory: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
    },

    accountCategory__header: {
        width: "100%",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",

        padding: "8px",
    },

    accountCategory__title: {
        fontFamily: "Lato, sans-serif",
        fontSize: "18px",
        fontWeight: "normal",
        lineHeight: "100%",
        margin: "0px",

        color: "#0C0E29",
    },

    accountCategory__total: {
        fontFamily: "Lato, sans-serif",
        fontSize: "18px",
        fontWeight: "normal",
        lineHeight: "100%",
        margin: "0px",

        color: "#0C0E29",
    },

    accountCategory__accounts: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
    },

    account: {
        width: "100%",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "8px",

        fontSize: "16px",
        fontFamily: "Lato, sans-serif",

        backgroundColor: "transparent",
        color: "#0C0E29",

        border: "none",
        borderRadius: "4px",

        opacity: "0.6",

        padding: "8px",

        transition: "all 0.2s ease-in-out",

        "&:hover": {
            backgroundColor: "#F4F4F4",

            width: "calc(100%)",

            cursor: "pointer",
        },
    },

    account__highlighted: {
        backgroundColor: "#C3996B",
        color: "white !important",
        opacity: "1",

        "&:hover": {
            backgroundColor: "#a77d4f",
        },
    },

    account__title: {
        margin: "0px",
    },

    chip__green: {
        width: "fit-content",
        height: "24px",

        display: "inline-flex",
        alignItems: "center",

        padding: "0px 12px",
        fontSize: "14px",
        fontWeight: "normal",
        fontFamily: "Lato, sans-serif",
        lineHeight: "100%",

        borderRadius: "12px",

        backgroundColor: "#E2F8E0",
        color: "#3AB037",

        marginLeft: "8px",
    },

    chip__red: {
        width: "fit-content",
        height: "24px",

        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",

        padding: "0px 12px",
        fontSize: "14px",
        fontWeight: "normal",
        fontFamily: "Lato, sans-serif",
        lineHeight: "100%",

        borderRadius: "12px",

        backgroundColor: "#FBE5E5",
        color: "#D84949",

        marginLeft: "8px",
    },

    report__total: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "8px",

        borderTop: "1px solid #EDEDED",
        paddingTop: "16px",

        margin: "0px 8px 8px 8px",
    },

    total__title: {
        display: "flex",
        alignItems: "center",
        gap: "8px",

        fontFamily: "Lato, sans-serif",
        fontSize: "18px",
        fontWeight: "normal",
        margin: "0px",

        color: "#0C0E29",
    },

    total__breakdown: {
        fontSize: "14px",
        opacity: "0.8",
    },

    total__price: {
        fontFamily: "Lato, sans-serif",
        fontSize: "18px",
        fontWeight: "normal",
        margin: "0px",

        color: "#0C0E29",
    },

    reportContainer__accountDetail: {
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "white",
        borderRadius: "8px",
        border: "none",
        transition: "width 0.2s ease-in-out, opacity 0.2s ease-in-out, padding 0.2s ease-in-out",
        width: "0px", // Initial state: hidden
        opacity: 0,
        padding: "16px 0",
    },

    active: {
        width: "560px", // Expand to fit content
        opacity: 1, // Fully visible
        padding: "16px", // Restore padding
        border: "1px solid #EDEDED",
    },

    accountDetail__header: {
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: "8px",
        marginBottom: "16px",
    },

    accountDetail__title: {
        // no wrapping
        whiteSpace: "nowrap",
    },

    accountDetail__closeButton: {
        backgroundColor: '#F5F5F5',
        border: 'none',
        cursor: 'pointer',

        padding: '3px !important',
        height: '25px !important',

        borderRadius: '3px',

        transition: 'all 0.2s',

        '&:hover': {
            backgroundColor: '#D84949',
            color: '#F5F5F5',
        },

        '&:focus-visible': {
            outline: '#4654FB auto 1px',
        },
    },

    accountDetail__content: {
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        gap: "16px",
    },

    accountDetail__details: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
    },

    accountDetail__detail: {
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "8px",

        whiteSpace: "nowrap",
        overflow: "hidden",
    },

    accountDetail__detailTitle: {
        fontFamily: "Lato, sans-serif",
        fontSize: "16px",
        margin: "0px",

        color: "#0C0E29",

        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },

    accountDetail__detailPrice: {
        fontFamily: "Lato, sans-serif",
        fontSize: "16px",
        margin: "0px",

        color: "#0C0E29",
    },

    accountDetail__total: {
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "8px",

        fontFamily: "Lato, sans-serif",
        fontSize: "16px",
        margin: "0px",

        color: "#0C0E29",

        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",

        borderTop: "1px solid #EDEDED",
        paddingTop: "8px",
    },

    accountDetail__disclaimer: {
        width: "100%",

        backgroundColor: "#F4F4F4",
        borderRadius: "8px",

        padding: "8px",
    },

    accountDetail__disclaimerText: {
        fontFamily: "Lato, sans-serif",
        fontSize: "14px",
        margin: "0px",

        color: "#0C0E29",
    },

    devExDataTableContainer: {
        width: "100%",
        boxSizing: "border-box",

        display: "flex",
        flexDirection: "column",
        gap: "16px",

        backgroundColor: "white",
        borderRadius: "8px",

        padding: "16px",
        marginTop: "16px",
    },

    devExDataTable__header: {
        width: "100%",

        display: "flex",
        gap: "16px",
    },

    devExDataTable__title: {
        fontFamily: "Lato, sans-serif",
        fontSize: "24px",
        margin: "0px",

        color: "#0C0E29",
    },

    devExDataTable__secondaryButton: {
        height: "32px",
        padding: "0 16px",

        border: "1px solid #C3996B",
        borderRadius: "4px",

        fontSize: "16px",
        color: "#C3996B",
        backgroundColor: "transparent",

        transition: "all 0.2s ease-in-out",

        userSelect: "none",

        "&:hover": {
            backgroundColor: "#a77d4f",
            color: "white",
            border: "1px solid #a77d4f",
            cursor: "pointer",
        }
    },

    emptyStateContainer: {
        width: "100%",
        height: "100%",

        position: "absolute",
        left: "0px",
        top: "0px",
        zIndex: "998",

        backgroundColor: "rgba(255, 255, 255, 0.6)",
        backdropFilter: "blur(8px)", // Apply blur to background elements

        borderRadius: "8px",

        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },

    emptyStateCard: {
        maxWidth: "320px",
        boxSizing: "border-box",

        padding: "16px",

        display: "flex",
        flexDirection: "column",

        alignItems: "center",

        backgroundColor: "white",

        border: "1px solid #EDEDED",
        borderRadius: "8px",
    },

    emptyStateCard__title: {
        fontFamily: "Lato, sans-serif",
        fontSize: "16px",
        color: "rgba(12, 14, 41, 1)",

        margin: "0px",
        marginBottom: "8px",
    },

    emptyStateCard__subtitle: {
        fontFamily: "Lato, sans-serif",
        fontSize: "14px",
        color: "rgba(12, 14, 41, 0.8)",

        textAlign: "center",

        margin: "0px",
    },

    primaryIconButton: {
        height: "40px",
        padding: "0 16px",
        paddingLeft: "8px",

        border: "none",
        borderRadius: "4px",

        fontSize: "16px",
        color: "white",
        backgroundColor: "#C3996B",

        display: "flex",
        gap: "8px",
        alignItems: "center",

        transition: "all 0.2s ease-in-out",

        "&:hover": {
            backgroundColor: "#a77d4f",
            color: "white",
            cursor: "pointer",
        }
    },

    // Filter on graph ---------------------------------------------------------------------------------------



    buttonContainer: {
        display: "flex",
        justifyContent: "flex-end",

        gap: "16px",
    },

    switchContainer__label: {
        fontSize: '16px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',
        color: "#0C0E29",

        gap: "8px !important",
        margin: "0px !important",

        userSelect: "none",
    },

    primaryButton: {
        height: "40px",
        padding: "0 16px",

        border: "none",
        borderRadius: "4px",

        fontSize: "16px",
        color: "white",
        backgroundColor: "#C3996B",

        transition: "all 0.2s ease-in-out",

        userSelect: "none",

        "&:hover": {
            backgroundColor: "#a77d4f",
            cursor: "pointer",
        }
    },

    secondaryButton: {
        height: "40px",
        padding: "0 16px",

        border: "1px solid #C3996B",
        borderRadius: "4px",

        fontSize: "16px",
        color: "#C3996B",
        backgroundColor: "transparent",

        transition: "all 0.2s ease-in-out",

        userSelect: "none",

        "&:hover": {
            backgroundColor: "#a77d4f",
            color: "white",
            border: "1px solid #a77d4f",
            cursor: "pointer",
        }
    },
}));